// Football scoring and roster presets (RULES_SCHEMA.md §19).

import type { RosterPreset, ScoringConfigLike, ScoringPreset } from '@fsp/sports-core';

type Unit = ScoringConfigLike['units'][number];
type Bonus = ScoringConfigLike['bonuses'][number];
type Tier = ScoringConfigLike['tiers'][number];

const u = (stat: string, points: number, positions: readonly string[] | null = null): Unit => ({
  stat,
  points,
  per: 1,
  rounding: 'none',
  positions,
});
const bonus = (stat: string, threshold: number, points: number): Bonus => ({
  stat,
  threshold,
  points,
  scope: 'game',
  positions: null,
});

const pointsAllowed: Tier = {
  stat: 'dst_pts_allowed',
  scope: 'game',
  positions: null,
  bands: [
    { min: 0, max: 0, points: 10 },
    { min: 1, max: 6, points: 7 },
    { min: 7, max: 13, points: 4 },
    { min: 14, max: 20, points: 1 },
    { min: 21, max: 27, points: 0 },
    { min: 28, max: 34, points: -1 },
    { min: 35, max: null, points: -4 },
  ],
};

const baseUnits: Unit[] = [
  u('pass_yd', 0.04),
  u('pass_td', 4),
  u('pass_int', -2),
  u('pass_2pt', 2),
  u('rush_yd', 0.1),
  u('rush_td', 6),
  u('rush_2pt', 2),
  u('rec_yd', 0.1),
  u('rec_td', 6),
  u('rec_2pt', 2),
  u('fum_lost', -2),
  u('fum_rec_td', 6),
  u('ret_td', 6),
  u('fg_0_19', 3),
  u('fg_20_29', 3),
  u('fg_30_39', 3),
  u('fg_40_49', 4),
  u('fg_50p', 5),
  u('fg_miss', -1),
  u('xp_made', 1),
  u('xp_miss', -1),
  u('dst_sack', 1),
  u('dst_int', 2),
  u('dst_fum_rec', 2),
  u('dst_ff', 1),
  u('dst_td', 6),
  u('dst_safety', 2),
  u('dst_blk', 2),
  u('dst_ret_td', 6),
];

const baseBonuses: Bonus[] = [
  bonus('pass_yd', 300, 3),
  bonus('pass_yd', 400, 2),
  bonus('rush_yd', 100, 3),
  bonus('rush_yd', 200, 2),
  bonus('rec_yd', 100, 3),
  bonus('rec_yd', 200, 2),
];

function config(id: string, units: Unit[], bonuses: Bonus[] = baseBonuses): ScoringConfigLike {
  return { presetId: id, units, bonuses, tiers: [pointsAllowed], decimals: 2 };
}

// DEFENSIVE PLAYERS' RULES (Q11): ESPN's published IDP scoring, the one big
// site that publishes its numbers (PLAN-2026-10-18-defensive-players §1). A
// league of defensive players has no team defense, so its rules carry none of
// the team defense's and no points allowed.
const idpUnits: Unit[] = [
  u('idp_tkl_solo', 1.5),
  u('idp_tkl_ast', 0.75),
  u('idp_tkl_loss', 2),
  u('idp_sack', 4),
  u('idp_int', 5),
  u('idp_pass_def', 1.5),
  u('idp_ff', 4),
  u('idp_fum_rec', 4),
  u('idp_td', 6),
  u('idp_safety', 2),
  u('idp_blk', 2),
];
const offenseUnits = baseUnits.filter((x) => !x.stat.startsWith('dst_'));

function idpConfig(id: string, units: Unit[]): ScoringConfigLike {
  return { presetId: id, units, bonuses: baseBonuses, tiers: [], decimals: 2 };
}

// THE NAMES ARE PLAIN WORDS (PLAN-2026-10-18-scoring-anyone-can-read §2.7).
// The ids are the names experienced players use and stay, because stored
// leagues, the season harness, the lab and the projections name presets by
// id; nothing on screen lists the presets any more. The create form asks two
// questions (`scoring-layout.ts`) and these are what the answers give.
export const scoringPresets: readonly ScoringPreset[] = [
  {
    id: 'ppr',
    name: 'Points for catches',
    description: 'Every catch is worth a point, on top of its yards.',
    config: config('ppr', [...baseUnits, u('rec', 1)]),
  },
  {
    id: 'half-ppr',
    name: 'Half a point per catch',
    description: 'Every catch is worth half a point, on top of its yards.',
    config: config('half-ppr', [...baseUnits, u('rec', 0.5)]),
  },
  {
    id: 'standard',
    name: 'No points for catches',
    description: 'A catch earns its yards and any touchdown, nothing more.',
    config: config('standard', [...baseUnits]),
  },
  {
    id: 'te-premium',
    name: 'Tight end bonus',
    description: 'A point for every catch, and a point and a half for a tight end’s.',
    config: config('te-premium', [...baseUnits, u('rec', 1), u('rec', 1.5, ['TE'])]),
  },
  {
    // It said "for leagues that start two quarterbacks", which no league made
    // on the create form could: the form never asked about the roster.
    id: 'superflex-ppr',
    name: 'Six-point touchdown passes',
    description: 'Points for catches, and a touchdown pass is worth 6 rather than 4.',
    config: config('superflex-ppr', [
      ...baseUnits.filter((x) => x.stat !== 'pass_td'),
      u('pass_td', 6),
      u('rec', 1),
    ]),
  },
  {
    id: 'ppr-idp',
    name: 'Points for catches, defensive players',
    description:
      'Every catch is worth a point, and defenders score for tackles, sacks and takeaways.',
    config: idpConfig('ppr-idp', [...offenseUnits, u('rec', 1), ...idpUnits]),
  },
  {
    id: 'standard-idp',
    name: 'No points for catches, defensive players',
    description: 'A catch earns its yards, and defenders score for tackles, sacks and takeaways.',
    config: idpConfig('standard-idp', [...offenseUnits, ...idpUnits]),
  },
];

const starters = (extra: { slot: string; count: number }[] = [], bench = 6) => [
  { slot: 'QB', count: 1 },
  { slot: 'RB', count: 2 },
  { slot: 'WR', count: 2 },
  { slot: 'TE', count: 1 },
  { slot: 'FLEX', count: 1 },
  ...extra,
  { slot: 'K', count: 1 },
  { slot: 'DST', count: 1 },
  { slot: 'BN', count: bench },
];

const irStatuses = ['O', 'IR', 'PUP', 'NFI', 'SUS'];

export const rosterPresets: readonly RosterPreset[] = [
  {
    id: 'standard',
    name: 'Standard',
    slots: starters(),
    reserve: { slot: 'IR', count: 1, eligibleStatuses: irStatuses },
    taxi: null,
  },
  {
    id: 'superflex',
    name: 'Superflex',
    slots: starters([{ slot: 'SFLEX', count: 1 }], 7),
    reserve: { slot: 'IR', count: 2, eligibleStatuses: irStatuses },
    taxi: null,
  },
  {
    id: 'best-ball',
    name: 'Best Ball',
    slots: [
      { slot: 'QB', count: 1 },
      { slot: 'RB', count: 2 },
      { slot: 'WR', count: 3 },
      { slot: 'TE', count: 1 },
      { slot: 'FLEX', count: 2 },
      { slot: 'BN', count: 10 },
    ],
    reserve: null,
    taxi: null,
  },
  {
    // One of each and a flex over them, the usual start (4for4's primer), in
    // place of the team defense; a seventh bench spot for the defenders.
    id: 'idp',
    name: 'Defensive players',
    slots: [
      { slot: 'QB', count: 1 },
      { slot: 'RB', count: 2 },
      { slot: 'WR', count: 2 },
      { slot: 'TE', count: 1 },
      { slot: 'FLEX', count: 1 },
      { slot: 'K', count: 1 },
      { slot: 'DL', count: 1 },
      { slot: 'LB', count: 1 },
      { slot: 'DB', count: 1 },
      { slot: 'IDP', count: 1 },
      { slot: 'BN', count: 7 },
    ],
    reserve: { slot: 'IR', count: 1, eligibleStatuses: irStatuses },
    taxi: null,
  },
  {
    id: 'dynasty',
    name: 'Dynasty',
    slots: starters([{ slot: 'SFLEX', count: 1 }], 12),
    reserve: { slot: 'IR', count: 3, eligibleStatuses: irStatuses },
    taxi: { slot: 'TAXI', count: 3, maxYearsExperience: 2 },
  },
];
